import * as React from "react";
import * as chartPanelService from "source/models/services/chartPanelService";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IReportItem, ReportItem } from "source/models/report/reportItem";
import { ApplicationPermissionType, ConfirmationDialogResult, FrontPagePanelSize, ReportType } from "ui/Scripts/source/models/enums";
import { SaveDataObj } from "source/framework/saveData";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TaskPageFilterDrawerTaskTypeParameters } from "./TaskPageFilterDrawerTaskTypeParameters";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { DrawerContent } from "source/components/framework/Drawer/DrawerContent";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Box, Grid, TextField } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { ReportFilterPanelExtraParameters } from "source/components/framework/Report/ReportFilterPanelExtraParameters";
import { ReportFilterPanelShowInFrontPage } from "source/components/framework/Report/ReportFilterPanelShowInFrontPage";
import { ISaveReportFilters } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { ISavedChartPanel, SavedChartPanel } from "source/models/chart/savedChartPanel";
import { IChartItem } from "source/models/chart/chartItem";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { ITaskPageFilterDrawerState, TaskPageFilterDrawer } from "./TaskPageFilterDrawer";
import { getTaskGeneralFilterFromFilterState, ITaskPageFilterLists, ITaskPageFilterState, TaskStateIdFilterActive } from "./TaskPageUtils";
import { PageChartFilterDrawer } from "source/components/framework/Page/PageChartFilterDrawer";

export interface ITaskPageReportFilterPanelProp extends ITaskPageFilterState, ITaskPageFilterLists {
    classes?: string;
    chartPanelDrawerMode?: boolean;
    chartPanel?: ISavedChartPanel;
    chartItems?: IChartItem[];
    reservedChartPanelNames?: string[];
    report: IReportItem;
    panelSize?: number;
    panelVisible?: boolean;

    canRemoveSharedReports: boolean;
    onSaveReport: (saveAs: boolean, onGetReportSaveFilters: () => ISaveReportFilters) => void;
    onRemoveReport: (filterId: string) => void;
    onOk: (filterState: ITaskPageFilterState, parameters: ITaskDataRow, showInFrontPage: boolean) => ISaveReportFilters;
    onChartPanelSaved?: (chartPanel: ISavedChartPanel) => void;
    onChartPanelRemoved?: (chartPanelId: string) => void;
    onCancel: () => void;
}

interface ITaskPageReportFilterPanelState extends ITaskPageFilterDrawerState {
    taskTypes: ITaskEditTypeItem[];
    selectedChartName: string;
    chartPanelName: string;
    chartTaskType: ITaskEditTypeItem;

    panelSize: string;
    panelVisible: boolean;
    extraParameters: ITaskDataRow;
    showInFrontPage: boolean;
}

export class TaskPageReportFilterPanel extends React.Component<ITaskPageReportFilterPanelProp, ITaskPageReportFilterPanelState> {
    private orgStateHash: string = "";

    getReportByChartItem = (chartItem: IChartItem, generalFilter: any, reportFilter: any): IReportItem => {
        const result = ReportItem.createReportItem(chartItem?.id, "", ReportType.Pdf, chartItem?.taskType);
        result.taskDataRow = chartItem?.taskDataRow;
        result.generalFilter = generalFilter;
        result.reportFilter = reportFilter;
        result.copyReportParameterValuesFromSavedValues();
        return result;
    };
    
    getStateFromProps = (props: ITaskPageReportFilterPanelProp): ITaskPageReportFilterPanelState => {
        const taskTypes = props.chartPanelDrawerMode
            ? props.taskTypes.filter(i => i.charts.length > 0)
            : props.taskTypes;
        const selectedTaskTypeIds = props.chartPanelDrawerMode
            ? taskTypes.length > 0
                ? [taskTypes.find(i => i.id === props.chartPanel?.reportId)?.id ?? taskTypes[0].id]
                : []
            : props.selectedTaskTypeIds;
        return {
            ...props,
            ...TaskPageFilterDrawerTaskTypeParameters.getStateChangeFromTaskType(TaskPageFilterDrawerTaskTypeParameters.getNewTaskType(taskTypes, selectedTaskTypeIds), props.selectedTaskTypeHeaderFields, props.selectedTaskTypeFields),
            taskTypes,
            selectedTaskTypeIds,
            panelSize: ((props.chartPanelDrawerMode ? props.panelSize : null) ?? FrontPagePanelSize.OneXOne).toString(10),
            panelVisible: (props.chartPanelDrawerMode ? props.panelVisible : null) ?? false,
            extraParameters: props.report?.taskType && props.report?.taskDataRow ? new TaskDataRow(props.report.taskDataRow) : null,
            showInFrontPage: (props.chartPanelDrawerMode ? props.chartPanel?.showInFrontPage : props.report?.showInFrontPage) ?? false,
            selectedChartName: props.chartPanelDrawerMode ? props.chartPanel?.chartId : "",
            chartPanelName: props.chartPanelDrawerMode ? props.chartPanel?.reportTitle : "",
            chartTaskType: taskTypes.length > 0 && selectedTaskTypeIds.length === 1
                ? taskTypes.find(i => i.id === selectedTaskTypeIds[0])
                : null
        };
    };

    constructor(props: ITaskPageReportFilterPanelProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getStateHash(props, this.state);
    }

    componentDidUpdate(prevProps: ITaskPageReportFilterPanelProp, prevState: ITaskPageReportFilterPanelState): void {
        const props = this.props;
        if (!prevProps || (prevProps.report?.getHash() ?? "") === (props.report?.getHash() ?? "") || (prevProps.chartPanel?.getHash() ?? "") === (props.chartPanel?.getHash() ?? "")) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getStateHash(props, state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "chartPanelName") {
            this.setState({ chartPanelName: value });
        }
    };

    handleTogglePanelVisible = () => {
        this.setState({ panelVisible: !this.state.panelVisible });
    };

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeStateIds = (selectedStateIds: string[]) => {
        this.setState({
            selectedStateIds
        });
    };

    handleChangePriorityIds = (selectedPriorityIds: string[]) => {
        this.setState({
            selectedPriorityIds
        });
    };

    handleChangeTaskTypeIds = (selectedTaskTypeIds: string[]) => {
        const props = this.props;
        const state = this.state;
        const chartTaskType = selectedTaskTypeIds.length === 1
            ? state.taskTypes.find(i => i.id === selectedTaskTypeIds[0])
            : null;
        this.setState({
            ...TaskPageFilterDrawerTaskTypeParameters.getChangedStateAdterChangeTaskTypeIds(props, selectedTaskTypeIds),
            chartTaskType,
            selectedChartName: chartTaskType && chartTaskType.charts.length > 0 ? chartTaskType.charts[0].name : ""
        });
    };

    handleChangePersonInChargeUserCodes = (selectedPersonInChargeUserCodes: string[]) => {
        this.setState({
            selectedPersonInChargeUserCodes
        });
    };

    handleChangeHandlerUserCodes = (selectedHandlerUserCodes: string[]) => {
        this.setState({
            selectedHandlerUserCodes
        });
    };

    handleSetFormParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const stateChange = TaskPageFilterDrawerTaskTypeParameters.getFormParametersState(this.state, taskDataRowCols);
        if (!stateChange) return;
        this.setState(stateChange);
    };

    handleSetHeaderParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const stateChange = TaskPageFilterDrawerTaskTypeParameters.getExtraHeaderParametersState(this.state, taskDataRowCols);
        if (!stateChange) return;
        this.setState(stateChange);
    };

    handleRefreshParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        const state = this.state;
        if (!state.extraParameters) return;
        const extraParameters = new TaskDataRow(state.extraParameters);
        extraParameters.cols = taskDataRowCols;
        this.setState({ extraParameters });
    };

    getStateHash = (props: ITaskPageReportFilterPanelProp, state: ITaskPageReportFilterPanelState): string => {
        const result = new SaveDataObj();
        result.append("selectedChartName", state.selectedChartName);
        result.append("chartPanelName", state.chartPanelName);
        result.append("panelSize", state.panelSize);
        result.append("panelVisible", state.panelVisible);
        result.append("selectedTaskTypeFields", state.selectedTaskTypeFields ? state.selectedTaskTypeFields : null);
        result.append("selectedTaskTypeHeaderFields", state.selectedTaskTypeHeaderFields ? state.selectedTaskTypeHeaderFields : null);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedPriorityIds", state.selectedPriorityIds);
        result.append("selectedTaskTypeIds", state.selectedTaskTypeIds);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedPersonInChargeUserCodes", state.selectedPersonInChargeUserCodes);
        if (props.report?.taskType && props.report?.taskDataRow) {
            result.append("parameters", state.extraParameters.cols.map(i => state.extraParameters.getTaskDataRowColUiValue(i.field, props.report.taskType)));
        }
        result.append("showInFrontPage", state.showInFrontPage);
        return result.hash;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (props.chartPanelDrawerMode) {
                if (!state.chartPanelName) {
                    AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                    return resolve(false);
                }
                if (props.reservedChartPanelNames.find(i => i.equalIgnoreCase(state.chartPanelName))) {
                    AppUtils.showErrorMessage(Translations.NameIsAlreadyInUse);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    handleSaveReport = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        if (!saveAs) {
            props.onOk(state, state.extraParameters, state.showInFrontPage);
            await this.props.onSaveReport(false, null);
        } else {
            await this.props.onSaveReport(true, () => {
                return props.onOk(state, state.extraParameters, state.showInFrontPage);
            });
        }
    };

    handleClear = () => {
        const props = this.props;
        const state = this.state;
        const extraParameters = state.extraParameters ? new TaskDataRow(state.extraParameters) : null;
        if (extraParameters && props.report?.taskType) {
            extraParameters.initializeToTaskType(props.report?.taskType);
        }
        const selectedTaskTypeIds = [state.taskTypes[0].id];
        const chartTaskType = selectedTaskTypeIds.length === 1
            ? state.taskTypes.find(i => i.id === selectedTaskTypeIds[0])
            : null;
        // console.log("clear", {
        //     ...TaskPageFilterDrawer.getClearedParametersState(props),
        //     selectedTaskTypeIds,
        //     extraParameters,
        //     chartTaskType
        // });
        this.setState({
            ...TaskPageFilterDrawer.getClearedParametersState(props),
            selectedTaskTypeIds,
            extraParameters,
            chartTaskType
        });
    };

    // #region ChartPanel
    saveChartPanel = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return;
        //Update chartPanel
        const chartPanel = new SavedChartPanel(props.chartPanel);
        chartPanel.id = state.chartPanelName;
        chartPanel.reportTitle = state.chartPanelName;
        chartPanel.size = state.panelSize.toInteger();
        chartPanel.visible = state.panelVisible;
        chartPanel.reportId = state.chartTaskType.id;
        chartPanel.chartId = state.selectedChartName;
        chartPanel.generalFilter = getTaskGeneralFilterFromFilterState(state);
        chartPanel.reportFilter = {};
        chartPanel.showInFrontPage = state.showInFrontPage;
        //Set SaveData
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", ApplicationPermissionType.Task);
        saveData.append("orgId", props.chartPanel?.id);
        saveData.append("item", chartPanel);
        //Call server
        const result = await AppUtils.callService(() => chartPanelService.saveChartPanel(saveData.obj));
        if (!result) return null;
        props.onChartPanelSaved(chartPanel);
    };

    handleRemoveChartPanel = async () => {
        const props = this.props;
        if (props.chartPanel?.isNew()) return;
        const confirmationResult = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveChartPanelParameter, props.chartPanel?.reportTitle));
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = new SaveDataObj();
        saveData.append("applicationPermissionType", ApplicationPermissionType.Task);
        saveData.append("id", props.chartPanel?.id);
        const result = await AppUtils.callService(() => chartPanelService.removeChartPanel(saveData.obj), false);
        if (!result) return;
        props.onChartPanelRemoved(props.chartPanel?.id);
    };
    // #end region ChartPanel

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        if (props.onOk) {
            props.onOk(state, state.extraParameters, state.showInFrontPage);
        } else if (props.onChartPanelSaved) {
            this.saveChartPanel();
        }
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getStateHash(this.props, this.state), this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const showChartProperties = props.chartPanelDrawerMode && !!state.chartTaskType;
        const content = <>
            {!!props.onSaveReport && !!props.onRemoveReport &&
                <FilterSearchField
                    filter={props.report}
                    canRemoveSharedFilters={props.canRemoveSharedReports}
                    isReport={true}
                    onSaveFilter={this.handleSaveReport}
                    onRemoveFilter={props.onRemoveReport}
                />
            }
            {showChartProperties &&
                <>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.PanelName} required margin={0}>
                                <TextField type="text" name="chartPanelName" title={Translations.PanelName} inputProps={{ maxLength: 50 }} value={state.chartPanelName} required={true} onChange={this.handleChange} />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={6}>
                            <FormField title="&nbsp;" fullWidth>
                                <FormCheckBox
                                    title={Translations.PanelVisible}
                                    checked={state.panelVisible}
                                    onChange={this.handleTogglePanelVisible}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.PanelSize} fullWidth>
                                <IdTitleDropdown
                                    selectedId={state.panelSize}
                                    items={[
                                        { id: "1", title: Translations.PanelSize1 },
                                        { id: "2", title: Translations.PanelSize2 },
                                        { id: "5", title: Translations.PanelSize5 },
                                        { id: "3", title: Translations.PanelSize3 },
                                        { id: "4", title: Translations.PanelSize4 },
                                        { id: "6", title: Translations.PanelSize6 }
                                    ]}
                                    onChange={(panelSize) => this.setState({ panelSize })}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </>
            }
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.TaskType}>
                        <IdTitleDropdown
                            selectedId={state.selectedTaskTypeIds.length > 0 ? state.selectedTaskTypeIds[0] : ""}
                            items={state.taskTypes.map(i => { return { id: i.id, title: i.code }; })}
                            onChange={(id) => this.handleChangeTaskTypeIds([id])}
                        />
                    </FormField>
                </Grid>
                {(showChartProperties && !!state.chartTaskType) &&
                    <Grid item sm={6}>
                        <FormField title={Translations.Chart}>
                            <IdTitleDropdown
                                selectedId={state.selectedChartName}
                                items={state.chartTaskType.charts.map(i => { return { id: i.name, title: i.title }; })}
                                onChange={(id) => this.setState({ selectedChartName: id })}
                            />
                        </FormField>
                    </Grid>
                }
                <Grid item sm={6}>
                    <FormField title={Translations.State}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedStateIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            toggleSingleSelectIds={[TaskStateIdFilterActive]}
                            values={props.states}
                            onChange={this.handleChangeStateIds} />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.Priority}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedPriorityIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.priorities}
                            onChange={this.handleChangePriorityIds} />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.Unit}>
                        <TreeCheckboxDropdown
                            required={true}
                            selectedIds={state.selectedUnitIds}
                            values={props.units}
                            onChange={this.handleChangeUnitIds} />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.PersonInCharge}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedPersonInChargeUserCodes}
                            toggleAll={true}
                            values={props.personsInCharge}
                            onChange={this.handleChangePersonInChargeUserCodes} />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.TaskHandler}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedHandlerUserCodes}
                            toggleAll={true}
                            values={props.handlers}
                            onChange={this.handleChangeHandlerUserCodes} />
                    </FormField>
                </Grid>
            </GridRow>
            <TaskPageFilterDrawerTaskTypeParameters
                taskTypes={state.taskTypes}
                taskType={state.taskType}
                headerParameters={state.headerParameters}
                formParameters={state.formParameters}
                onSetHeaderParameters={this.handleSetHeaderParameters}
                onSetFormParameters={this.handleSetFormParameters}
            />
            <ReportFilterPanelExtraParameters
                report={props.report}
                extraParameters={state.extraParameters}
                onChange={(extraParameters: ITaskDataRow) => this.setState({ extraParameters })}
            />
            {(showChartProperties || (!props.chartPanelDrawerMode && !!state.taskType)) &&
                <Box sx={{ marginTop: state.extraParameters || state.headerParameters || state.formParameters ? 0 : "1rem" }}>
                    <ReportFilterPanelShowInFrontPage
                        title={props.chartPanelDrawerMode ? Translations.ShowChartInFrontPage : Translations.ShowReportRowAmountInFrontPage}
                        showInFrontPage={state.showInFrontPage}
                        onChangeShowInFrontPage={(showInFrontPage: boolean) => this.setState({ showInFrontPage })}
                    />
                </Box>
            }
        </>;

        if (props.chartPanelDrawerMode) {
            return (
                <PageChartFilterDrawer
                    title={props.chartPanel?.getTitle()}
                    buttons={
                        <FilterButtons
                            chartPanelPropertiesMode
                            marginTop="0"
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                            onRemove={this.handleRemoveChartPanel}
                            onSave={this.handleSave}
                        />
                    }
                    onClose={() => this.handleCancel(false)}
                >
                    {content}
                </PageChartFilterDrawer>
            );
        }

        return (
            <DrawerContent
                content={content}
                actions={
                    <FilterButtons
                        chartPanelPropertiesMode={props.chartPanelDrawerMode}
                        onCancel={undefined}
                        onClear={this.handleClear}
                        onSave={this.handleSave}
                    />
                }
            />
        );
    }
}